<template>
  <div>
    <div v-if="!isUpdate" class="p-3 text-right">
      <BaseButton
        v-if="!isAddingCompany"
        text="Switch to Company"
        @click="changeToAddSubCompany"
      />
      <BaseButton
        v-else
        text="Switch to a Subcontractor"
        @click="changeToAddSubCompany"
      />
    </div>

    <form @submit.stop.prevent="form.handleSubmit()">
      <div v-if="!isAddingCompany && !isUpdate">
        <span class="font-semibold">User Type</span>
        <BaseCheckbox
          v-model="noaccess"
          label="No access user"
          description="Unheck if sub will have access to admin portal"
        />
      </div>

      <form.Field
        name="name"
        :validators="{
          onBlur: yup.string().required('Name is required')
        }"
      >
        <template #default="{ field }">
          <BaseInput
            v-model="(field.state.value as string)"
            label="Name"
            :name="field.name"
            :error="field.state.meta.errors && field.state.meta.errors[0] as string"
            @blur="field.handleBlur"
            @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
          />
        </template>
      </form.Field>

      <form.Field
        name="email"
        :validators="{
          onBlur: yup.string().required('Email is required').email('Email is invalid'),
        }"
      >
        <template #default="{ field }">
          <BaseInput
            v-model="(field.state.value as string)"
            :name="field.name"
            type="email"
            label="Email"

            :error="field.state.meta.errors && field.state.meta.errors[0] as string"
            @blur="field.handleBlur"
            @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
          />
        </template>
      </form.Field>

      <form.Field
        v-if="!isAddingCompany && !noaccess && !isUpdate"
        name="password"
        :validators="{
          onBlur: yup.string().min(8, 'Password must be at least 8 characters').required('Password is required')
        }"
      >
        <template #default="{ field }">
          <BaseInputPassword
            v-model="(field.state.value as string)"
            :name="field.name"
            label="Password"
            :error="field.state.meta.errors && field.state.meta.errors[0] as string"
            @blur="field.handleBlur"
            @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
          />
        </template>
      </form.Field>

      <form.Field
        name="phone"
        :validators="{
          onChange: yup.string().optional().matches(/^[0-9() -]*$/, 'Phone number is invalid'),
        }"
      >
        <template #default="{ field }">
          <BaseInputPhone
            v-model="(field.state.value as string)"
            :name="field.name"
            label="Phone"
            :error="field.state.meta.errors && field.state.meta.errors[0] as string"
            @blur="field.handleBlur"
            @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
            @change="(e) => e !== field.state.value && field.handleChange(e)"
          />
        </template>
      </form.Field>

      <form.Field
        v-if="isAddingCompany"
        name="address"
      >
        <template #default="{ field }">
          <AddressAutoComplete
            :name="field.name"
            :value="field.state.value as string"
            label="Address"
            @blur="field.handleBlur"
            @input="(e) => field.handleChange(e)"
          />
        </template>
      </form.Field>

      <form.Field
        v-if="!isAddingCompany"
        name="companyId"
      >
        <template #default="{ field }">
          <BaseSelect
            v-model="(field.state.value as string)"
            :name="field.name"
            :options="companies"
            label="Company"
            @blur="field.handleBlur"
            @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
          />
        </template>
      </form.Field>

      <form.Field
        v-if="!isAddingCompany"
        name="trade"
        :validators="{
          onChange: yup.string().required('Trade is required')
        }"
      >
        <template #default="{ field }">
          <BaseSelect
            v-model="(field.state.value as string)"
            :name="field.name"
            label="Trade"
            :options="trades"
            :error="field.state.meta.errors && field.state.meta.errors[0]"
            @blur="field.handleBlur"
            @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
          />
        </template>
      </form.Field>

      <hr class="mt-6">

      <div class="flex justify-end gap-2 mt-3">
        <form.Subscribe>
          <template #default="{ canSubmit, isSubmitting }">
            <BaseButton
              variant="base"
              text="Cancel"
              :to="{ name: 'ListSubcontractors' }"
              :disabled="isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
            />

            <BaseButton
              variant="primary"
              text="Save"
              type="submit"
              :disabled="!canSubmit || isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting }"
            />
          </template>
        </form.Subscribe>
      </div>
    </form>
  </div>
</template>

<script lang="ts" setup>
import { ref, PropType } from 'vue'
import { Subcontractor } from '@/models/subcontractor'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseInputPassword from '@/components/base/BaseInputPassword.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import AddressAutoComplete from '@/components/base/AddressAutoComplete.vue'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import FirebaseService from '@/services/FirebaseService'
import { httpsCallable } from 'firebase/functions'
import BaseInputPhone from '@/components/base/BaseInputPhone.vue'

const props = defineProps({
  sub: {
    type: Object as PropType<Subcontractor>,
    default: () => new Subcontractor()
  },
  trades: {
    type: Array as PropType<{ value: string; label: string }[]>,
    required: true
  },
  companies: {
    type: Array as PropType<{ value: string; label: string }[]>,
    required: true
  },
  saveFunction: {
    type: Function,
    required: true
  },
  isUpdate: {
    type: Boolean,
    default: false
  }
})


const sub = ref({ ...props.sub, password: '' })
const noaccess = ref(true)
const isAddingCompany = ref(props.sub.isCompany)

const form = useForm({
  defaultValues: sub.value,
  validatorAdapter: yupValidator(),
  onSubmit: async ({ value }) => {
    const sub = Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined))

    if (isAddingCompany.value) {
      sub.isCompany = true
      delete sub.password
      await props.saveFunction(sub as unknown as Subcontractor)
    } else {
      sub.isCompany = false
      const password = noaccess.value ? null : sub.password
      delete sub.password
      await props.saveFunction(sub as unknown as Subcontractor)

      if (password) {
        const functions = await FirebaseService.functions()
        const createUser = httpsCallable(functions, 'user-createV2')
        await createUser({
          name: sub.name,
          email: sub.email,
          password: password,
          roles: ['subcontractor']
        })
      }
    }
  }
})


const changeToAddSubCompany = () => {
  isAddingCompany.value = !isAddingCompany.value

  if (isAddingCompany.value) {
    form.deleteField('trade')
    form.deleteField('companyId')
    form.deleteField('password')
  } else {
    form.deleteField('address')
  }
}
</script>
